// An exploration's own reply or seed (docs/exploration-mode.md X22).
//
// The page that sent a message, had one answered (Try again, Answer it,
// X21) or asked for a seed learns from its own stream when the model is
// done: the stream's last event empties the lock line and turns the
// controls back on. A stream that stops before that (the connection
// dropped, the browser gave up waiting) would leave the page locked, so the
// page then asks again as one opened meanwhile does (live.ts), until the
// model is done, and says what such a page says: the lock line's "You can
// keep typing; …" and Check again, and the seed line's Check again, which
// the server served it hidden. So does a page whose message, answer or
// seed was refused (409) because someone else's reply or seed holds the
// exploration, or because the conversation moved on from the message it
// offered to answer: it learns when that ends, or how it stands now. One
// refused because the exploration closed (an ending elsewhere, or a
// Create spec on the issue's page while it was empty, X14) stays as it
// is, the refusal in the list and the text in the box (X10): the closed
// exploration has no box, so bringing the page up to date would take
// what was typed off it. Once the model is done the server serves the
// emptied lock line with a sentence saying what the run left as its
// summary ("Finished. bob's reply is at the end of the conversation."),
// which live.ts says once, as it says any summary served anew (FR-UI-14).
//
// What is typed on the page is kept in this browser's session storage as
// it is typed (X10), under the exploration's address and id (the
// composer's data-unsent-key): the message box while it holds text not yet
// sent, and the seed's box while it differs from the seed it was served
// with, with the seed it was typed over. A reload, a followed link or Back
// then loses neither. Back on the page, the message goes back in an empty
// box; the seed's edits go back in its box over the same seed, with the
// line "Your edits to the seed are kept in this browser." and a button,
// "Use the model's seed instead", that puts the model's seed back. Edits
// typed over an earlier seed are never put over a newer one: they are
// shown under it to copy, as seed.ts shows edits a new seed replaced
// (X24), and so is a Confirm the server refused because a newer seed was
// written while it was reviewed, which it serves there (X25); the page
// bringing itself up to date keeps them shown. Confirm sends the id of the
// seed its box was served with; a refused Confirm whose box keeps the text
// as sent goes on naming the seed that text was typed over as the page
// brings in a newer one, so confirming it again is refused too (X11).
// Leaving the page asks first while either box holds unsent text,
// except by the exploration's own forms that end it (Confirm, Start spec
// without seed, Abandon), which are a choice already made, and by a link
// to the exploration's own address (Check again), which brings the text
// back. Send forgets the message once the server takes it, and Confirm
// forgets the seed's edits. Without the bundle the page works as it
// always did.
//
// An empty exploration offers questions to start with (X30): each
// button puts its question in the message box, without sending it, ready
// to edit or send. Since only the bundle does that, the server serves them
// hidden and the bundle shows them.
//
// Past four messages, **Latest message ↓** beside the issue's fold leads
// to the newest message the server served (X20); the bundle keeps it
// leading to the newest as replies come in. Nothing scrolls the page as
// it opens, but for a link to Abandon exploration… (the verdict's line,
// FR-ISS-16), which opens End this exploration around it and goes there.

import { announceAfterLoad } from "./announce";
import { fitBox } from "./composer";
import { AFTER_REFRESH, BEFORE_REFRESH, type LiveRefresh, watch } from "./live";
import { stayed } from "./motion";
import { alreadySent } from "./reply";

declare const htmx: {
  on: (event: string, handler: (evt: CustomEvent) => void) => void;
};

/** The forms whose requests stream: Send's, Try again's and Answer it's
 * (X21), and Generate seed's. */
const STREAMING_FORMS = new Set([
  "explore-send",
  "explore-answer",
  "seed-form",
]);

/** The parts of the page that bring themselves up to date: the issue's
 * state in the head, and the conversation with its controls. */
const PARTS = ["issue-chips", "exploration"];

/** How often it asks, as the server has a page opened meanwhile ask. */
const SECONDS = 3;

/** The lock line (`explore.rs` `LOCK_STATUS`), and the attribute carrying
 * its one-line summary once the model is done (FR-UI-14). */
const LOCK_STATUS = "lock-status";
const LIVE_SUMMARY = "data-live-summary";

/** What only a page that asks again says (`explore.rs` `WATCHING_ONLY`),
 * which the page streaming a reply or a seed is served hidden (X22). */
const WATCHING_ONLY = ".watching-only[hidden]";

/** The header on a refusal because the exploration is closed
 * (`explore.rs` `CLOSED_HEADER`). */
const CLOSED_HEADER = "specter-closed";

/** Whether the lock line still says the model is writing. */
function stillBusy(): boolean {
  return (
    document.getElementById(LOCK_STATUS)?.classList.contains("busy") ?? false
  );
}

export function installExploreHooks(): void {
  htmx.on("htmx:finally:request", (evt: CustomEvent) => {
    const ctx = evt.detail?.ctx as
      | {
          sourceElement?: Element;
          response?: { status?: number; headers?: Headers };
        }
      | undefined;
    const form = ctx?.sourceElement;
    if (!(form instanceof HTMLFormElement) || !STREAMING_FORMS.has(form.id)) {
      return;
    }
    // X10, X14: closed, it stays as it is, the text in the box to copy.
    if (ctx?.response?.headers?.has(CLOSED_HEADER)) return;
    if (!stillBusy() && ctx?.response?.status !== 409) return;
    // X22: the page asks again from now on, so it says so, with Check again.
    for (const el of document.querySelectorAll<HTMLElement>(WATCHING_ONLY)) {
      el.hidden = false;
    }
    // FR-UI-14: what the page opened with said how an earlier run ended.
    // The page now waits on another, whose sentence may be the same words
    // ("Finished. bob's reply …" twice), so it is dropped from what the
    // page last had: the next served, even at the first ask, is said.
    document.getElementById(LOCK_STATUS)?.removeAttribute(LIVE_SUMMARY);
    const parts = PARTS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    watch(parts, SECONDS);
  });
}

/** The exploration page's message box (X21) and seed's box (X23). */
const MESSAGE_BOX = "message-text";
const SEED_BOX = "seed-text";

/** The line over the seed's box when kept edits go back in it, which
 * names the seed it is served with (data-seed-id). */
const SEED_RESTORED = "seed-restored";

/** Where edits typed over an earlier seed are shown to copy (seed.ts). */
const EARLIER_PLACE = "earlier-seed-box";
const EARLIER_FIELD = "earlier-seed";

/** Confirm's hidden field naming the seed the box's text was typed over,
 * which the server refuses once a newer seed was written (X11, X25). */
const SEED_OVER = 'input[name="seed_id"]';

/** What this browser keeps of an exploration's page. */
export interface Unsent {
  /** The message box's text, not yet sent. */
  message: string;
  /** The seed's box, when it differs from the seed it was served with. */
  seed: string;
  /** The id of the seed those edits were typed over. */
  seedId: string;
}

const unsentKey = (key: string) => `specter-explore-unsent:${key}`;

function readUnsent(key: string): Unsent | null {
  try {
    const raw = sessionStorage.getItem(unsentKey(key));
    if (raw === null) return null;
    const kept = JSON.parse(raw) as Partial<Unsent> | null;
    if (
      typeof kept?.message !== "string" ||
      typeof kept.seed !== "string" ||
      typeof kept.seedId !== "string"
    ) {
      return null;
    }
    return { message: kept.message, seed: kept.seed, seedId: kept.seedId };
  } catch {
    return null;
  }
}

function writeUnsent(key: string, unsent: Unsent): void {
  try {
    if (unsent.message === "" && unsent.seed === "") {
      sessionStorage.removeItem(unsentKey(key));
    } else {
      sessionStorage.setItem(unsentKey(key), JSON.stringify(unsent));
    }
  } catch {
    // No storage here: leaving still asks first.
  }
}

function box(id: string): HTMLTextAreaElement | null {
  const el = document.getElementById(id);
  return el instanceof HTMLTextAreaElement ? el : null;
}

/** The id of the seed the seed's box is served with, if there is one. */
function seedId(): string {
  return document.getElementById(SEED_RESTORED)?.dataset.seedId ?? "";
}

/** What the page holds that isn't sent: the message box's text, unless it
 * is what was just sent from it, and the seed's box's when it isn't the
 * seed it was served with. */
export function unsentOnPage(): Unsent {
  const message = box(MESSAGE_BOX);
  const typed = message?.value ?? "";
  const seed = box(SEED_BOX);
  const edited = seed !== null && seed.value !== seed.defaultValue;
  return {
    message:
      typed.trim() !== "" && !alreadySent(MESSAGE_BOX, typed) ? typed : "",
    seed: edited ? seed.value : "",
    seedId: edited ? seedId() : "",
  };
}

/** The forms that end the exploration and leave the page: Confirm, Start
 * spec without seed and Abandon. */
function endingForm(form: HTMLFormElement): "confirm" | "other" | null {
  const path = new URL(form.action, window.location.href).pathname;
  if (path.endsWith("/explore/confirm")) return "confirm";
  if (path.endsWith("/explore/promote") || path.endsWith("/explore/abandon")) {
    return "other";
  }
  return null;
}

/** Put kept edits back in the seed's box, over the seed they were typed
 * over, with the line that says so; edits typed over another seed are
 * shown under this one to copy. Returns whether they went in the box. */
function restoreSeed(kept: Unsent): boolean {
  const seed = box(SEED_BOX);
  const line = document.getElementById(SEED_RESTORED);
  if (!seed || !line || kept.seed === "" || seed.value !== seed.defaultValue) {
    return false;
  }
  if (kept.seed === seed.value) return false;
  if (kept.seedId === seedId()) {
    seed.value = kept.seed;
    // What follows the box's text (seed.ts's warning) hears of it.
    seed.dispatchEvent(new Event("input", { bubbles: true }));
    line.hidden = false;
    announceAfterLoad(line.textContent?.replace(/\s+/g, " ").trim() ?? "");
    return true;
  }
  const place = document.getElementById(EARLIER_PLACE);
  const field = box(EARLIER_FIELD);
  if (place?.hidden && field) {
    field.value = kept.seed;
    place.hidden = false;
  }
  return false;
}

export function installUnsentHooks(): void {
  const composer = document.querySelector<HTMLElement>("[data-unsent-key]");
  const key = composer?.dataset.unsentKey ?? "";
  if (key === "") return;
  // The exploration's own address, which brings the text back.
  const own = key.slice(0, key.lastIndexOf("#"));

  const guard = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    // Browsers from before preventDefault was enough.
    event.returnValue = true;
    // Kept by answering Stay: the link or form that asked to leave is
    // usable again (FR-UI-11), as on the editor.
    window.setTimeout(stayed, 0);
  };
  let guarded = false;
  const guardLeaving = (on: boolean) => {
    if (on === guarded) return;
    guarded = on;
    if (on) window.addEventListener("beforeunload", guard);
    else window.removeEventListener("beforeunload", guard);
  };

  // Once the page goes by one of its own endings or back to its own
  // address, nothing more is asked.
  let leaving = false;
  const note = () => {
    if (leaving) return;
    const unsent = unsentOnPage();
    writeUnsent(key, unsent);
    guardLeaving(unsent.message !== "" || unsent.seed !== "");
  };

  // Back on the page: the message into an empty box, the seed's edits as
  // restoreSeed puts them.
  const kept = readUnsent(key);
  if (kept) {
    const message = box(MESSAGE_BOX);
    if (message && message.value === "" && kept.message !== "") {
      message.value = kept.message;
      // The box grows to hold it, as it does as it is typed in (X21).
      fitBox(message);
    }
    restoreSeed(kept);
  }
  note();

  // A different page may have asked for a new seed. Polling normally
  // preserves a dirty textarea's value, but that would leave the old seed
  // under the new seed's facts and ID. Capture its edits before patching,
  // then show them to copy beside the new model seed, as a stream does.
  let replaced: { text: string; edited: boolean } | null = null;
  // X25: edits shown to copy stay shown, also those the server put there
  // (a Confirm a newer seed refused), which the exploration's own address
  // serves hidden and empty.
  let shownEarlier: string | null = null;
  // X25: the seed a refused Confirm's kept text was typed over.
  let keptOver: string | null = null;
  document.addEventListener(BEFORE_REFRESH, (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.id !== "exploration"
    ) {
      return;
    }
    replaced = null;
    const place = document.getElementById(EARLIER_PLACE);
    shownEarlier =
      place && !place.hidden ? (box(EARLIER_FIELD)?.value ?? null) : null;
    keptOver = null;
    // X25: a refused Confirm's notice identifies text the server returned
    // exactly as submitted. Its box keeps that submission after polling,
    // even when a different seed was generated in the meantime, and
    // Confirm keeps naming the seed it was typed over, so confirming it
    // over a newer seed is refused rather than dropping that seed (X11).
    if (document.querySelector(`[data-kept-box="${SEED_BOX}"]`)) {
      keptOver =
        document.querySelector<HTMLInputElement>(SEED_OVER)?.value ?? null;
      return;
    }
    const incoming = (event as CustomEvent<LiveRefresh>).detail.incoming;
    const seed = box(SEED_BOX);
    const next = incoming.querySelector(`#${SEED_BOX}`);
    const nextId = incoming.querySelector<HTMLElement>(`#${SEED_RESTORED}`)
      ?.dataset.seedId;
    if (!seed || !next || nextId === undefined || nextId === seedId()) return;
    replaced = { text: seed.value, edited: seed.value !== seed.defaultValue };
  });
  document.addEventListener(AFTER_REFRESH, (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.id !== "exploration"
    ) {
      return;
    }
    const previous = replaced;
    replaced = null;
    const over = document.querySelector<HTMLInputElement>(SEED_OVER);
    if (keptOver !== null && over) over.value = keptOver;
    keptOver = null;
    const place = document.getElementById(EARLIER_PLACE);
    const field = box(EARLIER_FIELD);
    if (shownEarlier !== null && place?.hidden && field) {
      field.value = shownEarlier;
      place.hidden = false;
    }
    shownEarlier = null;
    const seed = box(SEED_BOX);
    if (!previous || !seed) return;
    seed.value = seed.defaultValue;
    const restored = document.getElementById(SEED_RESTORED);
    if (restored) restored.hidden = true;
    if (previous.edited && previous.text !== seed.value && place && field) {
      field.value = previous.text;
      place.hidden = false;
    }
    // Update kept typing and the seed's design-notes warning for what is
    // now displayed, without assigning old edits to the new seed's ID.
    seed.dispatchEvent(new Event("input", { bubbles: true }));
  });

  document.addEventListener("input", (event) => {
    const target = event.target;
    if (
      target instanceof HTMLTextAreaElement &&
      (target.id === MESSAGE_BOX || target.id === SEED_BOX)
    ) {
      note();
    }
  });

  // "Use the model's seed instead": the seed the box was served with goes
  // back in it, and the kept edits are forgotten.
  document.addEventListener("click", (event) => {
    const reset = (event.target as Element | null)?.closest?.(
      "[data-seed-reset]",
    );
    const seed = box(SEED_BOX);
    if (!reset || !seed) return;
    seed.value = seed.defaultValue;
    const line = document.getElementById(SEED_RESTORED);
    if (line) line.hidden = true;
    seed.dispatchEvent(new Event("input", { bubbles: true }));
    seed.focus();
  });

  // Send's request: the message is forgotten as it goes, and kept again
  // if it is still in the box when the request ends (a refusal); a new
  // seed, the stream's end or the page bringing itself up to date can
  // change either box.
  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    // htmx sends it, so the browser's own submit is stopped.
    if (form.id === "explore-send") {
      writeUnsent(key, { ...unsentOnPage(), message: "" });
      return;
    }
    // An ending stopped before it went (a second press) leaves nothing.
    const ending = endingForm(form);
    if (ending === null || event.defaultPrevented) return;
    if (ending === "confirm") {
      writeUnsent(key, { ...unsentOnPage(), seed: "", seedId: "" });
    }
    leaving = true;
    guardLeaving(false);
  });
  htmx.on("htmx:finally:request", note);
  htmx.on("htmx:after:settle", note);

  // A link to the exploration's own address (Check again) brings the text
  // back, so it goes without asking.
  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const link = (event.target as Element | null)?.closest?.("a[href]");
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.target && link.target !== "_self") return;
    if (link.origin !== window.location.origin || link.pathname !== own) {
      return;
    }
    // A jump within this page is a scroll, not a reload.
    if (link.pathname === window.location.pathname && link.hash !== "") return;
    leaving = true;
    guardLeaving(false);
  });

  // Back from the back/forward cache: the page is usable again.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    leaving = false;
    note();
  });
}

/** The questions an empty exploration offers to start with (X30),
 * each a button under its opening line. */
const STARTER = "button[data-starter]";

/** Their group, under its label **Start with a question:**. Only the
 * bundle makes a question fill the box, so the server serves the group
 * hidden, as it serves the key hints, and the bundle shows it. */
const STARTERS = ".exploration-starters[hidden]";

/** Show the starting questions under `root` while there is a message box
 * for them to fill. */
export function showStarters(root: ParentNode): void {
  if (!box(MESSAGE_BOX)) return;
  for (const group of root.querySelectorAll<HTMLElement>(STARTERS)) {
    group.hidden = false;
  }
}

/** Put a starting question in the message box without sending it: in
 * place of what is there when the box is empty or holds another starting
 * question, else after what was typed, so nothing typed is lost. */
export function startWith(
  typed: string,
  question: string,
  starters: string[],
): string {
  const now = typed.trim();
  if (now === "" || starters.includes(now)) return question;
  return `${typed.trimEnd()}\n\n${question}`;
}

export function installStarters(): void {
  showStarters(document);
  // A part the page brings up to date in place (X22) may put in a fresh,
  // hidden copy of them.
  document.addEventListener(AFTER_REFRESH, (event) => {
    if (event.target instanceof Element) showStarters(event.target);
  });
  document.addEventListener("click", (event) => {
    const button = (event.target as Element | null)?.closest?.(STARTER);
    const message = box(MESSAGE_BOX);
    if (!(button instanceof HTMLButtonElement) || !message) return;
    const starters = Array.from(
      document.querySelectorAll<HTMLButtonElement>(STARTER),
      (b) => b.textContent?.trim() ?? "",
    );
    message.value = startWith(
      message.value,
      button.textContent?.trim() ?? "",
      starters,
    );
    // What keeps the page's typing (X10) hears of it, as of typing.
    message.dispatchEvent(new Event("input", { bubbles: true }));
    message.focus();
    message.setSelectionRange(message.value.length, message.value.length);
  });
}

/** **Latest message ↓** under the exploration's head (X20), which the
 * server points at the newest message it served. */
const LATEST = "a.latest-message";

/** The exploration's conversation, which a reply streams into and the
 * page brings up to date in place (X22). */
const EXPLORATION = "exploration";

/** The newest stored message in the list under `root` as a fragment,
 * `#message-12`, or null while it has none; the live reply and alerts
 * aren't messages. */
export function newestMessage(root: ParentNode): string | null {
  const stored = root.querySelectorAll<HTMLElement>(
    '#messages > li.message[id^="message-"]',
  );
  const last = stored[stored.length - 1];
  return last ? `#${last.id}` : null;
}

/** Keep **Latest message ↓** leading to the newest message as replies
 * end on the page and as it brings a teammate's in (X20). */
export function installLatest(): void {
  const link = document.querySelector<HTMLAnchorElement>(LATEST);
  const exploration = document.getElementById(EXPLORATION);
  if (!link || !exploration) return;
  new MutationObserver(() => {
    const newest = newestMessage(exploration);
    if (newest && link.getAttribute("href") !== newest) {
      link.setAttribute("href", newest);
    }
  }).observe(exploration, { childList: true, subtree: true });
}

/** **Abandon exploration…**, inside **End this exploration**, which the
 * page serves folded. */
const ABANDON = "abandon-exploration";

/**
 * X20, FR-ISS-16: the verdict's line under the head ends "End this
 * exploration › Abandon exploration…", a link to `#abandon-exploration`.
 * Its target is inside the folded **End this exploration**, which not
 * every browser opens for a link, so the fold is opened here, as the page
 * opens and as the link is followed, and Abandon exploration… brought into
 * view; it stays closed itself, asking first (FR-UI-18). Without the
 * bundle the browser's own jump does what the browser does.
 */
export function openLinkedAbandon(): void {
  if (location.hash.slice(1) !== ABANDON) return;
  const target = document.getElementById(ABANDON);
  const fold = target?.parentElement?.closest<HTMLDetailsElement>(
    "details.explore-endings",
  );
  if (!target || !fold) return;
  if (!fold.open) {
    fold.open = true;
    // Shown where it lands at once, not eased in from a little lower
    // (`details[open]` in the stylesheet), so the jump below stays put.
    for (const shown of fold.querySelectorAll(":scope > :not(summary)")) {
      for (const easing of shown.getAnimations()) easing.finish();
    }
  }
  target.scrollIntoView({ block: "start" });
}

installExploreHooks();
installUnsentHooks();
installStarters();
installLatest();
openLinkedAbandon();
addEventListener("hashchange", openLinkedAbandon);
